import { z } from 'zod';
import { baseModelSchema } from '../../../model/baseModel';
import { IPrinterFormValues } from './printerFormValues';

/**
 * Zod schema for validating printer data.
 * Extends the base model schema with printer-specific fields.
 * Represents a printer device in the system.
 *
 * @property {string} name - Name of the printer (max 255 characters)
 * @property {string} [defaultPaper] - Optional default paper type (max 255 characters)
 * @property {string} [ipAddress] - Optional IP address for the printer (IPv4 or IPv6, max 255 characters)
 * @property {number} [port] - Optional port number for the printer (0-65535)
 * @property {boolean} isActive - Whether the printer is active
 * @property {number} printServerId - ID of the print server this printer is connected to
 */
export const printerSchema = baseModelSchema.extend({
    name: z.string().nonempty('Name is required').max(255, 'Name can only be up to 255 characters long.').default(''),
    defaultPaper: z.string().max(255, 'Default Paper can only be up to 255 characters long.').optional(),
    ipAddress: z.string()
        .regex(
            /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$|^(?:(?:[0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}|(?:[0-9a-fA-F]{1,4}:){1,7}:|(?:[0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|(?:[0-9a-fA-F]{1,4}:){1,5}(?::[0-9a-fA-F]{1,4}){1,2}|(?:[0-9a-fA-F]{1,4}:){1,4}(?::[0-9a-fA-F]{1,4}){1,3}|(?:[0-9a-fA-F]{1,4}:){1,3}(?::[0-9a-fA-F]{1,4}){1,4}|(?:[0-9a-fA-F]{1,4}:){1,2}(?::[0-9a-fA-F]{1,4}){1,5}|[0-9a-fA-F]{1,4}:(?:(?::[0-9a-fA-F]{1,4}){1,6})|:(?:(?::[0-9a-fA-F]{1,4}){1,7}|:)|fe80:(?::[0-9a-fA-F]{0,4}){0,4}%[0-9a-zA-Z]{1,}|::(?:ffff(?::0{1,4}){0,1}:){0,1}(?:(?:25[0-5]|(?:2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(?:25[0-5]|(?:2[0-4]|1{0,1}[0-9]){0,1}[0-9])|(?:[0-9a-fA-F]{1,4}:){1,4}:(?:(?:25[0-5]|(?:2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(?:25[0-5]|(?:2[0-4]|1{0,1}[0-9]){0,1}[0-9]))$/,
            'IP address must be a valid IPv4 or IPv6 address'
        )
        .optional()
        .nullable(),
    isActive: z.boolean().default(true),
    printServerId: z.number(),
});

/**
 * TypeScript interface representing a Printer object.
 * Generated from the Zod schema for type safety.
 */
export type IPrinter = z.infer<typeof printerSchema>;

/**
 * Creates a validated Printer object from form values.
 * Converts form values into a properly structured printer object.
 *
 * @param {IPrinterFormValues} formValues - The form data to convert
 * @returns {IPrinter} A validated printer object
 * @throws {ZodError} If validation fails
 */
export function Printer(formValues : IPrinterFormValues) : IPrinter {
    const { printServer } = formValues;

    return printerSchema.parse({
        ...formValues,
        printServerId: printServer?.value ? Number(printServer.value) : undefined,
    });
};
